import { createReducer, on } from '@ngrx/store';

import {
  ORBI_HOW_IT_WORKS_EXPLAINER,
  ORBI_HOW_IT_WORKS_LABEL,
  ORBI_HOW_IT_WORKS_MESSAGE_ID,
} from '@patient-ui/shared/constants';
import {
  OrbiFeedbackType,
  OrbiMessageType,
  RequestStatus,
} from '@patient-ui/shared/enums';
import { OrbiMessage } from '@patient-ui/shared/models';
import {
  generateOrbiSessionId,
  orbiMessageLocksChat,
  readOrbiFeedback,
  readOrbiSessionLock,
  writeOrbiFeedback,
  writeOrbiSessionLock,
} from '@patient-ui/shared/utils';

import { OrbiError, getOrbiUserFacingError } from './orbi-error';
import { OrbiActions } from './orbi.actions';
import * as AuthActions from '../auth/auth.actions';

export const orbiFeatureKey = 'orbi';

export interface OrbiState {
  /** Stable conversation id; never empty (seeded with a UUID v4). */
  sessionId: string;
  /**
   * The ACTIVE conversation's messages only. Conversation history is owned by
   * the backend and fetched on demand (PCSA-13169) — it is NOT stored locally.
   */
  messages: OrbiMessage[];
  /**
   * The status of the most recent send OUTCOME. Kept for the request conventions
   * the other slices share, but it cannot describe the thread on its own: with
   * consecutive sends allowed it flips to Success the moment the FIRST answer
   * lands while a second is still generating. What is outstanding is read off
   * `messages` instead — one reserved slot per send, see `orbiLoadingTurn` and
   * `selectOrbiSendPending`.
   */
  sendMessageRequestStatus: RequestStatus;
  error: OrbiError | null;
  /** Last user message text + context, stashed for `retryLastMessage`. */
  lastUserMessage: string;
  lastRequestContext: string[];
  /**
   * Latched when a locking (non-soft) guardrail fires; stays true for the rest
   * of the session and is cleared only by a new conversation or logout. The
   * lock EXPERIENCE (disabled inputs, banners) is PCSA-13170 — this is the data.
   */
  chatLockedForSession: boolean;
  /**
   * When the ACTIVE conversation was created, as the service's UTC instant —
   * `null` for a conversation started in this browser session (design review
   * 08/20, "add conversation date to chat").
   *
   * It has to live here because no message carries a date: `OrbiMessage` has no
   * timestamp field, and `GET /history` puts the only one the service exposes on
   * the CONVERSATION (`OrbiHistoryMetadata.createdAt`), not on its turns. So the
   * thread can state a date for a resumed conversation and only for a resumed
   * one — `resumeConversation` is the single writer, and `null` is not a missing
   * value but the accurate answer for a conversation the service has not stamped
   * yet, which the view renders as "Today" (`formatOrbiChatDate`).
   *
   * NOT rehydrated from sessionStorage like the lock below: a browser refresh
   * mints a fresh session id and an empty thread, so there is no conversation
   * left for the old date to belong to.
   */
  startedAt: string | null;
  /**
   * Feedback submitted per agent response, keyed by `messageId` (PCSA-13171).
   * Records the sentiment so the response row can lock + highlight the chosen
   * action and disable the other after a successful submission.
   *
   * REHYDRATED from sessionStorage like the lock above (PCSA-13312): a rated
   * response has to stay rated across a browser refresh, or re-opening that
   * conversation from history offers both thumbs again on an answer the patient
   * has already scored. Cleared on logout.
   */
  feedbackByMessageId: Record<string, OrbiFeedbackType>;
}

/**
 * Factory so each reset gets a fresh, non-empty sessionId. The session lock
 * (PCSA-13170) is REHYDRATED from sessionStorage rather than reset: a locking
 * guardrail holds for the whole login session, so it must survive both a new
 * conversation (`startNewConversation`) and a browser refresh (fresh store).
 * It is cleared only by `createLoggedOutOrbiState` (logout) — see below.
 *
 * Submitted feedback rehydrates the same way (PCSA-13312). Carrying it through
 * `startNewConversation` too is deliberate rather than incidental: message ids
 * are unique per response, so keeping the map cannot latch a row in the new
 * conversation, and dropping it would un-rate the responses of any earlier
 * conversation the patient then re-opens from history.
 */
export function createOrbiInitialState(): OrbiState {
  return {
    sessionId: generateOrbiSessionId(),
    messages: [],
    sendMessageRequestStatus: RequestStatus.NotSent,
    error: null,
    lastUserMessage: '',
    lastRequestContext: [],
    chatLockedForSession: readOrbiSessionLock(),
    // A conversation that starts here has no server timestamp yet, and this is
    // also what retires a resumed conversation's date on `startNewConversation`.
    startedAt: null,
    feedbackByMessageId: readOrbiFeedback(),
  };
}

/**
 * Fully reset state for logout (PCSA-13170): clears the persisted session lock
 * so a NEW authenticated session starts unlocked, and the persisted feedback
 * with it — the ratings are the previous patient's, and the backend is their
 * durable record (PCSA-13312).
 */
export function createLoggedOutOrbiState(): OrbiState {
  writeOrbiSessionLock(false);
  writeOrbiFeedback({});
  return {
    ...createOrbiInitialState(),
    chatLockedForSession: false,
    feedbackByMessageId: {},
  };
}

export const OrbiInitialState: OrbiState = createOrbiInitialState();

/**
 * The "thinking" placeholder a send RESERVES for its own answer (PCSA-13312).
 *
 * Each send appends one of these directly under its user turn, and the outcome
 * REPLACES it in place — the model the mobile app uses (`OrbiChatService.
 * addMessage` captures `loadingMessageIndex` and later assigns
 * `_messages[loadingMessageIndex]`). That is what pairs each answer with the
 * message it answers: with two questions outstanding the thread reads
 * question → answer → question → answer, instead of both questions stacking and
 * both answers landing after them, which is what was reported on 08/17 ("the
 * answer from ai agent to be for each message").
 *
 * It also means the bubble can never be duplicated or left dangling — the
 * property the previous synthesized-in-the-selector turn was chosen for — because
 * a slot is created and consumed by exactly one send.
 */
const orbiLoadingTurn = (): OrbiMessage =>
  ({
    text: '',
    type: OrbiMessageType.Loading,
    route: 'text',
    guardrailTriggered: false,
  } as OrbiMessage);

/**
 * The index of the slot the answer now arriving belongs to: the OLDEST
 * outstanding placeholder.
 *
 * Sound because the send effect flattens with `concatMap`, so requests run
 * serially and outcomes arrive in the order the messages were sent — the first
 * unfilled slot is always the one being answered. No correlation id has to be
 * threaded through the action for it. (Were this ever moved to `mergeMap`, answers
 * could arrive out of order and the id would become necessary — hence this note
 * rather than a silent assumption.)
 *
 * Returns -1 when no slot is outstanding, which the callers treat as "append",
 * so an unexpected extra outcome shows the patient the answer rather than
 * swallowing it.
 */
const oldestPendingIndex = (messages: OrbiMessage[]): number =>
  messages.findIndex((message) => message.type === OrbiMessageType.Loading);

/** Replace the slot at `index`, or append when there is none to fill. */
const fillSlot = (
  messages: OrbiMessage[],
  index: number,
  turn: OrbiMessage
): OrbiMessage[] => {
  if (index < 0) {
    return [...messages, turn];
  }
  const next = [...messages];
  next[index] = turn;
  return next;
};

export const orbiReducer = createReducer(
  OrbiInitialState,
  on(OrbiActions.sendMessage, (state, { userMessage, requestContext }) => ({
    ...state,
    sendMessageRequestStatus: RequestStatus.Pending,
    error: null,
    lastUserMessage: userMessage,
    lastRequestContext: requestContext,
    // The user turn AND the slot its answer will land in, so a second message
    // sent while the first is generating gets its own thinking bubble under
    // itself rather than sharing one at the foot of the thread (PCSA-13312).
    messages: [
      ...state.messages,
      {
        text: userMessage,
        type: OrbiMessageType.User,
        route: 'text',
        guardrailTriggered: false,
      } as OrbiMessage,
      orbiLoadingTurn(),
    ],
  })),
  on(OrbiActions.sendMessageSuccess, (state, { message, sessionId }) => {
    // The lock is latched FIRST, and regardless of which conversation the answer
    // belongs to: a locking guardrail (EMERGENCY / HARM) holds for the whole
    // LOGIN session, not for one thread, so an answer that arrives after the
    // user moved on still has to lock the chat.
    const chatLockedForSession =
      state.chatLockedForSession ||
      orbiMessageLocksChat(
        message.guardrailTriggered,
        message.guardrailType ?? message.returnType
      );

    // A STALE answer — the user started a new conversation (or resumed another)
    // while the agent was still thinking (PCSA-13312, 08/14 Vibe). Appending it
    // here is the reported bug: the response lands in the fresh thread with no
    // prompt above it, because the prompt was cleared with the old conversation
    // (it is still there when that conversation is reopened, since the backend
    // owns the history). Drop the turn; the slot it would have filled went with
    // the conversation the user left.
    if (sessionId !== state.sessionId) {
      return { ...state, chatLockedForSession };
    }

    return {
      ...state,
      sendMessageRequestStatus: RequestStatus.Success,
      error: null,
      // Into the slot this send reserved — directly under its own question.
      messages: fillSlot(
        state.messages,
        oldestPendingIndex(state.messages),
        message
      ),
      chatLockedForSession,
    };
  }),
  // Failure keeps messages + sessionId intact (does NOT break the chat flow).
  // Session-guarded like success: an error from a conversation the user has
  // already left must not surface as an error turn on the one now on screen.
  //
  // The error turn takes the failed send's OWN slot (PCSA-13312), so with two
  // messages outstanding the error sits under the question that failed while the
  // other keeps its thinking bubble — the same in-slot treatment mobile gives a
  // failed request. `canRetry` here is only whether the error KIND is retryable;
  // whether the affordance is actually offered is decided in
  // `selectOrbiThreadMessages`, which requires the error to be the final turn.
  on(OrbiActions.sendMessageFailure, (state, { error, sessionId }) => {
    if (sessionId !== state.sessionId) {
      return state;
    }
    const { text, canRetry } = getOrbiUserFacingError(error);
    return {
      ...state,
      sendMessageRequestStatus: RequestStatus.Failure,
      error,
      messages: fillSlot(state.messages, oldestPendingIndex(state.messages), {
        text,
        type: OrbiMessageType.Error,
        route: 'text',
        guardrailTriggered: false,
        canRetry,
      } as OrbiMessage),
    };
  }),
  // Resume a stored conversation (PCSA-13169): adopt its sessionId + messages
  // and carry its read-only (guardrail) lock state. Status resets so the input
  // behaves as a fresh, idle thread.
  on(
    OrbiActions.resumeConversation,
    (state, { sessionId, messages, locked, startedAt }) => ({
      ...state,
      sessionId,
      messages,
      // The resumed conversation's own creation date, so the thread dates it as
      // the history row does rather than as today (design review 08/20). Absent
      // when the caller has no metadata to give, which reads as an unstamped
      // conversation — the same fallback a brand-new one gets.
      startedAt: startedAt ?? null,
      sendMessageRequestStatus: RequestStatus.NotSent,
      // Any send still in flight belonged to the conversation being left, and its
      // outcome will now be dropped by the session guards. Its reserved slot goes
      // with it: `messages` is replaced wholesale by the resumed conversation, so
      // no thinking bubble can survive into the thread now on screen (PCSA-13312).
      error: null,
      lastUserMessage: '',
      lastRequestContext: [],
      chatLockedForSession: locked,
    })
  ),
  // Blocked by the Split-flag gate (PCSA-13165): no service call was made, so
  // revert the optimistic Pending + drop the just-appended user turn. Without
  // this the status would stay Pending forever (no success/failure follows).
  //
  // Session-guarded most strictly of the three (PCSA-13312): this outcome REMOVES
  // turns from the thread, so landing it on the wrong conversation would delete
  // messages the user can see rather than merely add one they cannot explain.
  //
  // It removes the blocked send's own slot AND the user turn it sits under, which
  // is a splice at that index rather than the trailing `slice(0, -1)` this used to
  // be: with a queue the blocked send is not necessarily the last thing in the
  // thread, and slicing the tail would delete somebody else's turn.
  on(OrbiActions.sendMessageBlocked, (state, { sessionId }) => {
    if (sessionId !== state.sessionId) {
      return state;
    }
    const slot = oldestPendingIndex(state.messages);
    return {
      ...state,
      sendMessageRequestStatus: RequestStatus.NotSent,
      error: null,
      messages:
        slot < 0
          ? state.messages
          : // The user turn is immediately above its own slot.
            [
              ...state.messages.slice(0, Math.max(0, slot - 1)),
              ...state.messages.slice(slot + 1),
            ],
    };
  }),
  // "How does this work?" (PCSA-13159 AC 6): append the explainer as a real
  // conversation TURN PAIR — the chip's label as the user's message, the approved
  // explainer as the agent's answer (design review 08/11: the Figma "About Ask AI"
  // frame draws a purple user bubble followed by an ordinary bordered response
  // with the copy/thumbs row, not an aside above the input).
  //
  // Composed here rather than sent: there is no transport for it, so no
  // Pending/Success cycle and no effect. `lastUserMessage` is deliberately NOT
  // stashed — that field feeds `retryLastMessage`, and a retry must resend the
  // user's last REAL question, never re-ask this synthetic one.
  //
  // `route: 'text'` + a `messageId` are what earn the response its feedback row
  // (the renderer requires both), which is what the frame draws. The id is fixed
  // (`ORBI_HOW_IT_WORKS_MESSAGE_ID`) so the row's submitted-sentiment latch has a
  // stable key, and so this guard can tell the pair is already in the thread.
  // The guard is defensive — appending retires the suggested prompts, so the chip
  // is gone by the time it could be activated twice.
  on(OrbiActions.explainHowItWorks, (state) =>
    state.messages.some(
      (message) => message.messageId === ORBI_HOW_IT_WORKS_MESSAGE_ID
    )
      ? state
      : {
          ...state,
          messages: [
            ...state.messages,
            {
              text: ORBI_HOW_IT_WORKS_LABEL,
              type: OrbiMessageType.User,
              route: 'text',
              guardrailTriggered: false,
            } as OrbiMessage,
            {
              text: ORBI_HOW_IT_WORKS_EXPLAINER,
              type: OrbiMessageType.Llm,
              route: 'text',
              messageId: ORBI_HOW_IT_WORKS_MESSAGE_ID,
              guardrailTriggered: false,
            } as OrbiMessage,
          ],
        }
  ),
  on(OrbiActions.startNewConversation, () => createOrbiInitialState()),
  // Drop the failed turn PAIR so the retry resubmits cleanly: the
  // `retryLastMessage$` effect re-dispatches `sendMessage`, which appends the user
  // turn and a fresh slot — without this the failed message would be duplicated.
  //
  // Both turns go, because the error now lives in the thread in the failed send's
  // own slot (PCSA-13312) rather than being synthesized at the foot of it: the
  // tail is `[…, user, error]`, so removing only one would leave an orphan. Mobile
  // does the same ("removes the latest error bubble and the user turn that led to
  // it, then resubmits").
  //
  // Two guards, both matching a precondition elsewhere:
  //  - `lastUserMessage` non-empty, the SAME test `retryLastMessage$` applies;
  //    otherwise a retry with nothing stashed would destructively remove turns and
  //    pin Pending here while the effect never re-dispatches — a stuck spinner.
  //  - the last turn IS the error. Retry is only ever offered on a trailing error
  //    turn (`selectOrbiThreadMessages`), so anything else means a later send is
  //    still outstanding and the pair to remove is not at the tail. Same guard
  //    mobile uses (`_messages.last.type != error` → return).
  //
  // The status is left Pending for the re-dispatch that follows in the SAME
  // dispatch stack (the effect uses `map`, not an async operator), so no render
  // happens in between and the thread never flickers through an empty state.
  on(OrbiActions.retryLastMessage, (state) => {
    const last = state.messages[state.messages.length - 1];
    if (
      state.lastUserMessage.trim().length === 0 ||
      last?.type !== OrbiMessageType.Error
    ) {
      return state;
    }
    return {
      ...state,
      sendMessageRequestStatus: RequestStatus.Pending,
      error: null,
      messages: state.messages.slice(0, -2),
    };
  }),
  // Record submitted feedback per response (PCSA-13171) so the row can lock +
  // highlight the chosen sentiment and disable the other action. Only recorded
  // on SUCCESS (a failed submission stays actionable so the user can retry).
  on(
    OrbiActions.submitOrbiFeedbackSuccess,
    (state, { messageId, feedbackType }) => ({
      ...state,
      feedbackByMessageId: {
        ...state.feedbackByMessageId,
        [messageId]: feedbackType,
      },
    })
  ),
  // On logout, fully reset with a brand-new sessionId (never reuse a stale id).
  on(AuthActions.logOutSuccess, () => createLoggedOutOrbiState())
);
